/**
 * Which Pinia stores Sentry is allowed to see.
 *
 * `createSentryPiniaPlugin()` defaults to `attachPiniaState: true` with an
 * identity `stateTransformer`, which attaches a JSON dump of *every* store to
 * every error event and mirrors the same state into `contexts.state` after
 * every action. It iterates `pinia.state.value` wholesale — not just the store
 * it was installed on.
 *
 * LingoTalk's schema is pseudonymous: there is no name or email column
 * anywhere, a patient is a UUID and a user is an opaque `external_id`. That
 * makes the stakes here different from the portal's, not lower. What the stores
 * hold is:
 *
 *   - **credentials** — the session token, the weekly support code;
 *   - **therapy content tied to a person** — which exercises a named patient
 *     has been assigned, which words they practised, and how they scored. No
 *     name sits next to it, but it is chosen by a therapist for a condition,
 *     and the practice that can read it already knows who the patient is.
 *
 * The rule is deny-by-default: a store not named in ALLOWED_STORES is dropped,
 * whether or not anyone remembered to list it in DENIED_STORES. DENIED_STORES
 * exists so the classification of every store is written down, and so
 * `sentryStoreAllowlist.spec.ts` can fail when a new store appears without a
 * decision having been made about it.
 *
 * Kept deliberately parallel to the portal's file of the same name.
 * See docs/error-reporting.md.
 */

/** Safe to send: no credentials, and nothing tied to an individual patient. */
export const ALLOWED_STORES = [
  // The public content library: topics and items everyone sees. Therapy
  // material, not anybody's therapy.
  'topics',
  'therapistTopics',
  // itemId -> cover image URL. Derived from the library above.
  'therapistCovers',
  // Counters for the offline queues. A number of pending rows, no contents.
  'runQueue',
  'deleteQueue',
  // Which exercise ids have a resumable snapshot on disk. Ids of library rows,
  // not of people.
  'resumableRuns',
  // Subscription/trial status and the days left on it. Commercial state, and
  // the first thing worth knowing when a purchase flow misbehaves.
  'entitlement',
  // The help drawer's copy and whether it is open. UI state.
  'help',
] as const

/** Deliberately withheld, with the reason. */
export const DENIED_STORES = {
  // The session token and the identity behind it.
  auth: 'session token and account identity',
  // Which patient the therapist build is currently working with, plus a
  // snapshot of their row.
  activePatient: 'the patient currently selected in the dock or embed',
  // A live credential: whoever holds it can reach that patient's support flow.
  'support-code': 'a weekly-rotating patient credential',
  // What this patient was assigned, i.e. what their therapist decided they
  // need to practise.
  exercises: 'exercises assigned to this patient',
  homework: 'homework assigned to this patient',
  // What they just practised and how they scored on it.
  lastRun: 'the results of a patient run',
  // Per-patient configuration: mic behaviour, difficulty, pacing. It describes
  // how this person is being treated.
  'patient-settings': 'per-patient therapy configuration',
  // Words and items a therapist is choosing, in flight, for someone.
  exerciseDraft: 'exercise content being composed for a patient',
  therapistExerciseDraft: 'exercise content being composed for a patient',
  // A live credential, and a stronger one than the support code: whoever holds
  // an unredeemed transfer code can take the whole account — every exercise,
  // every run, and any purchase on it. Ten minutes is plenty for a crash report
  // to be written, read and acted on, so this one must never leave the device.
  transfer: 'a live credential that transfers the entire account',
} as const

type StoreState = Record<string, unknown>

/**
 * Keeps only the allowlisted stores. Anything unrecognised is dropped, so the
 * failure mode of forgetting to classify a new store is "Sentry sees less",
 * never "Sentry sees a patient's therapy".
 */
export function allowlistStores(state: StoreState): StoreState {
  const safe: StoreState = {}

  for (const id of ALLOWED_STORES) {
    if (id in state) {
      safe[id] = state[id]
    }
  }

  return safe
}
